import './PaymentStep.scss';
import React, { useEffect, useRef, useState } from 'react';
import Color from 'color';
import moment from 'moment';
import 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid, useMediaQuery, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { includes, some } from 'lodash';
import { CheckCircleOutline } from '@mui/icons-material';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { asOrderPageData } from '../core/types';
import { useOrderPage } from '../orderPageContext/useOrderPage';
import { OrderPageTextField } from '../themedComponents/OrderPageTextField';
import { OrderPageButton } from '../themedComponents/OrderPageButton';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import { customerFacingPriceFormatter, priceFormatterFromCurrency } from '../utils';
import { bookingFeeAmount, getDepositInfo, isBookingFeeOn } from '../core/pricing';
import { OrderPagePriceBreakdown } from '../components/OrderPagePriceBreakdown';
import PaymentIframe from './PaymentIframe';
import {
  CodesService,
  GetPaymentFormRequestData,
  PassType,
  PaymentFormType,
  PaymentsService,
  Tip,
  TipType,
} from '../../api';
import { applyCodeErrorMessage } from '../../shared/errors/applyCodeErrors';
import { isRecurringMonthly } from '../../managePassesPage/utils/passUtils';
import { TipComponent } from './payments/TipComponent';
import { parseSquarePaymentForm, toPaymentUrl } from './payments/squarePaymentForm';
import { useStripeRenderProps } from './payments/useStripeRenderProps';
import RotatingLoader from '../../ui/RotatingLoader';
import { OrderPageIntent } from '../OrderPageIntent';
import { OrderFinalDetailsTable } from './OrderFinalDetailsTable';
import { useLanguage } from '../../utils/GeneralUtils';
import { OrderPageSelect } from '../themedComponents/OrderPageSelect';
import { FormStyledMenuItem } from './personalDetails/RenderDropDownDetail';

const PaymentStep = () => {
  const theme = useTheme();
  const language = useLanguage();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { orderPageState, setOrderPageState, setTip, sessionTtlMilliseconds } = useOrderPage();
  const { orderPageIntent } = orderPageState;

  const { account } = orderPageState;

  const { t } = useTranslation();
  const [isLoadingApplyCode, setIsLoadingApplyCode] = useState(false);
  const [shouldShowVoucherSuccess, setShouldShowCodeApplySuccess] = useState(false);
  const [voucherOrDiscountCode, setVoucherOrDiscountCode] = useState('');
  const [voucherError, setCodeApplyError] = useState<string | null>(null);
  const [loadingPaymentLink, setLoadingPaymentLink] = useState(false);
  const [paymentLinkFailed, setPaymentLinkFailed] = useState(false);
  const shouldEnablePaymentsSelection =
    !account.payment_integration_settings?.should_enable_payments_only_in_payment_links &&
    account.payment_integration_settings?.max_payments &&
    account.payment_integration_settings?.max_payments >= 0 &&
    account.payment_integration_settings?.min_amount_for_payments &&
    account.payment_integration_settings?.min_amount_for_payments <= orderPageState.details.price;
  const [shouldShowPaymentsSelector, setShouldShowPaymentsSelector] = useState(shouldEnablePaymentsSelection || false);
  const [selectedMaxPayments, setSelectedMaxPayments] = useState(1);
  const navigate = useNavigate();
  const shouldShowDuration = !!asOrderPageData(orderPageState.orderPage)?.show_duration;
  const courseSessions = orderPageState.availableDates
    ?.find((d) => d.date === orderPageState.dateAndTimeDetails.date)
    ?.times.find((t) => t.seconds === orderPageState.dateAndTimeDetails.time)?.courseSessions;
  const [paymentUrl, setPaymentUrl] = useState(orderPageState?.paymentUrl);
  const showPymentButton = useFeatureFlagEnabled('show-payment-button');

  const { stripeAccountId, stripe_integration_publishable_key } = useStripeRenderProps(account);
  const isSquareForm = !!parseSquarePaymentForm(paymentUrl);
  const isStripeForm = !!stripe_integration_publishable_key;
  const isExperiencePage = window.location.pathname.includes('/experience/');
  const shouldShowButtonForPayment =
    !isExperiencePage &&
    !isSquareForm &&
    !isStripeForm &&
    ((paymentUrl?.includes('meshulam') && isSmallScreen && !shouldShowPaymentsSelector) || showPymentButton);

  const depositInfo = getDepositInfo({
    widgetOrderPageData: orderPageState.orderPage,
    details: orderPageState.details,
    dateDetails: orderPageState.dateAndTimeDetails,
    account: orderPageState.account,
  });

  const getPaymentForm = async () => {
    setLoadingPaymentLink(true);
    setPaymentLinkFailed(false);
    // component only used in the us for refetching with tip
    const payload: GetPaymentFormRequestData = {
      language,
      account_id: orderPageState.account.id,
      experience_id: orderPageState.experience.id,
      order_page_id: orderPageState.orderPage.id,
      entity_id: orderPageState.order_id,
      payment_form_type:
        orderPageIntent === OrderPageIntent.GIFT_CARD || orderPageIntent === OrderPageIntent.MONETARY_GIFT_CARD
          ? PaymentFormType.VOUCHER
          : orderPageIntent === OrderPageIntent.BUY_PASS
          ? PaymentFormType.PASS
          : PaymentFormType.ORDER,
    };
    if (orderPageState?.tip?.value && orderPageState?.tip?.value > 0) {
      payload.tip = orderPageState?.tip;
    }
    if (selectedMaxPayments > 1) {
      payload.max_payments = selectedMaxPayments;
    }

    try {
      const url = toPaymentUrl(await PaymentsService.getPaymentForm(payload));
      setPaymentUrl(url);
      if (isSmallScreen && !parseSquarePaymentForm(url) && !isStripeForm) {
        window.location.href = url;
      }
      return true;
    } catch (e) {
      // Without this the loader below never clears: there is no payment url to render, so a
      // provider or account-config failure looks to the customer like a hung page.
      console.error(e);
      setPaymentLinkFailed(true);
      return false;
    } finally {
      setLoadingPaymentLink(false);
    }
  };
  useEffect(() => {
    if (!orderPageState?.tip) {
      return;
    }
    void getPaymentForm();
  }, [orderPageState?.tip]);

  const [discountedPrice, setDiscountedPrice] = useState(
    orderPageState?.discountedPrice ?? orderPageState?.details.price,
  );
  const [voucherAmountUsed, setVoucherAmountUsed] = useState<number | undefined>(orderPageState?.voucherAmountUsed);
  const [voucherBalanceLeft, setVoucherBalanceLeft] = useState<number | undefined>(orderPageState?.voucherBalanceLeft);
  const [isAutoApplyingVoucher, setIsAutoApplyingVoucher] = useState(
    !!orderPageState.prefilledVoucherCode && orderPageState.voucherAmountUsed === undefined,
  );

  const [dynamicSessionTtlMilliseconds, setDynamicSessionTtlMilliseconds] = useState<number | null>(null);
  const dynamicSessionTtlMillisecondsIntervalRef = useRef<number>();
  useEffect(() => {
    if (sessionTtlMilliseconds === null) {
      setDynamicSessionTtlMilliseconds(null);
      return;
    }

    setDynamicSessionTtlMilliseconds(sessionTtlMilliseconds);
    if (sessionTtlMilliseconds <= 0) {
      return;
    }

    dynamicSessionTtlMillisecondsIntervalRef.current = setInterval(() => {
      setDynamicSessionTtlMilliseconds((currentValue) => {
        if (currentValue === null) {
          clearInterval(dynamicSessionTtlMillisecondsIntervalRef.current);
          return null;
        }

        if (currentValue <= 1000) {
          clearInterval(dynamicSessionTtlMillisecondsIntervalRef.current);
          return 0;
        }

        return currentValue - 1000;
      });
    }, 1000) as unknown as number;

    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(dynamicSessionTtlMillisecondsIntervalRef.current);
    };
  }, [sessionTtlMilliseconds]);

  // Without the entity params the completed page fetches nothing, so it can neither show the order
  // number nor tell that an external voucher settled the order.
  const orderCompletedUrl = () => {
    const base = `/order/completed?account_id=${orderPageState.account.id}&order_page_id=${orderPageState.orderPage.id}`;
    return orderPageState.order_id ? `${base}&transaction_type=order&entity_id=${orderPageState.order_id}` : base;
  };

  const submitCode = async (code = voucherOrDiscountCode) => {
    setShouldShowCodeApplySuccess(false);
    setIsLoadingApplyCode(true);
    setCodeApplyError(null);

    try {
      const resp = await CodesService.applyCode({
        entity_id: orderPageState.order_id,
        code,
      });
      // The api sends null for discount codes; only a gift card sets these.
      const voucherUsed = resp.voucher_amount_used ?? undefined;
      const voucherBalance = resp.voucher_balance_left ?? undefined;
      setVoucherAmountUsed(voucherUsed);
      setVoucherBalanceLeft(voucherBalance);
      if (resp.payment_link) {
        setPaymentUrl(resp.payment_link);
      }
      if (resp.new_price === 0) {
        navigate(orderCompletedUrl());
      }
      if (resp.new_price) {
        setDiscountedPrice(resp.new_price);
      }
      setOrderPageState({
        ...orderPageState,
        ...(resp.payment_link ? { paymentUrl: resp.payment_link } : {}),
        ...(resp.new_price !== undefined ? { discountedPrice: resp.new_price } : {}),
        ...(voucherUsed !== undefined ? { priceBeforeVoucher: discountedPrice } : {}),
        voucherAmountUsed: voucherUsed,
        voucherBalanceLeft: voucherBalance,
      });
      setShouldShowCodeApplySuccess(true);
    } catch (applyError: any) {
      setCodeApplyError(applyCodeErrorMessage(applyError, t));
    } finally {
      setIsLoadingApplyCode(false);
      setIsAutoApplyingVoucher(false);
    }
  };

  const hasAutoSubmittedCodeRef = useRef(false);
  useEffect(() => {
    if (!orderPageState.prefilledVoucherCode || hasAutoSubmittedCodeRef.current) {
      return;
    }
    hasAutoSubmittedCodeRef.current = true;
    setVoucherOrDiscountCode(orderPageState.prefilledVoucherCode);
    if (orderPageState.voucherAmountUsed === undefined) {
      void submitCode(orderPageState.prefilledVoucherCode);
    }
  }, []);

  if (orderPageState.details.price === 0) {
    window.parent.location.href = orderCompletedUrl();
    return <Grid />;
  }

  // Discounts and gift certificates are rejected outright for recurring passes, so the field would
  // only ever fail. The start-date picker is hidden for the same reason.
  const isRecurringPassCheckout =
    orderPageIntent === OrderPageIntent.BUY_PASS &&
    isRecurringMonthly((orderPageState.orderPage as { pass_type?: PassType }).pass_type);

  const handleVoucherCodeChange = (event: any) => {
    setVoucherOrDiscountCode(event.target.value);
  };
  const shouldShowSandbox =
    paymentUrl &&
    !isSquareForm &&
    some(['sandbox', 'paymentsdev', 'sc_test', 'elibis'], (substring) => includes(paymentUrl, substring));

  function calculateTip(tip?: Tip, price?: number) {
    if (!tip || !price) {
      return 0;
    }
    if (tip.type === TipType.FIXED) {
      return tip.value;
    }
    return (price * tip.value) / 100;
  }

  const paymentTimerDuration =
    dynamicSessionTtlMilliseconds !== null ? moment.duration(dynamicSessionTtlMilliseconds, 'milliseconds') : null;

  const isBookingFeeActive = isBookingFeeOn(account);
  const isTipEnabled = !!asOrderPageData(orderPageState.orderPage)?.extra_price_component?.tip;
  const hasDepositRow = depositInfo !== null && orderPageIntent === OrderPageIntent.BUY_NOW;
  const formatAmount = (amount: number) => customerFacingPriceFormatter(amount, account) as string;

  const basePriceLabel = (defaultLabel: string) =>
    orderPageIntent === OrderPageIntent.MONETARY_GIFT_CARD
      ? t('order_page.giftCardAmount')
      : asOrderPageData(orderPageState.orderPage)?.show_price_as_deposit && depositInfo === null
      ? t('order_page.deposit')
      : defaultLabel;

  const renderPriceBreakdown = () => {
    // After a gift card, discountedPrice is already the remainder to charge.
    const hasVoucher = voucherAmountUsed !== undefined;
    const chargeBase = hasVoucher ? discountedPrice : hasDepositRow ? depositInfo.amount : discountedPrice;
    const priceBeforeGiftCard = hasVoucher ? orderPageState.priceBeforeVoucher ?? discountedPrice : discountedPrice;
    const bookingFee = bookingFeeAmount(chargeBase, account);
    const { tip } = orderPageState;
    const tipAmount = tip ? (tip.type === TipType.FIXED ? tip.value : Math.trunc((chargeBase * tip.value) / 100)) : 0;
    const laterAmount = hasDepositRow ? priceBeforeGiftCard - depositInfo.amount : 0;
    const laterFee = bookingFeeAmount(laterAmount, account);
    const sharedRows = [
      ...(hasVoucher ? [{ label: t('order_page.paidWithGiftCard'), amount: voucherAmountUsed, negative: true }] : []),
      ...(tipAmount > 0 ? [{ label: t('order_page.gratuity'), amount: tipAmount }] : []),
      { label: t('order_page.bookingFee'), amount: bookingFee },
    ];
    const discountRows =
      priceBeforeGiftCard !== orderPageState.details.price
        ? [{ label: t('order_page.breakdownPriceAfterDiscount'), amount: priceBeforeGiftCard }]
        : [];
    const rows = hasDepositRow
      ? [
          { label: t('order_page.fullPrice'), amount: orderPageState.details.price, muted: true },
          ...discountRows.map((row) => ({ ...row, muted: true })),
          { label: t('order_page.breakdownDeposit'), amount: depositInfo.amount },
          ...sharedRows,
        ]
      : [
          { label: basePriceLabel(t('order_page.breakdownPrice')), amount: orderPageState.details.price },
          ...discountRows,
          ...sharedRows,
        ];
    const notes = [
      ...(voucherAmountUsed !== undefined ? [t('order_page.noFeeOnGiftCard')] : []),
      ...(hasDepositRow && laterFee > 0
        ? [t('order_page.bookingFeeLater', { amount: formatAmount(laterAmount), fee: formatAmount(laterFee) })]
        : []),
      t('order_page.bookingFeeNonRefundable'),
    ];
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <OrderPagePriceBreakdown
          rows={rows}
          totalLabel={hasDepositRow ? t('order_page.totalToPayNow') : t('order_page.totalToPay')}
          total={chargeBase + tipAmount + bookingFee}
          notes={notes}
          account={account}
          withTax
        />
        {voucherAmountUsed !== undefined && (
          <Grid container gap={2}>
            <OrderPageTypography bold>{t('order_page.giftCardBalanceLeft')}</OrderPageTypography>
            <OrderPageTypography>{formatAmount(voucherBalanceLeft ?? 0)}</OrderPageTypography>
          </Grid>
        )}
      </Box>
    );
  };

  const orderDetailsTable = (
    <OrderFinalDetailsTable
      orderPageIntent={orderPageIntent}
      shouldShowDuration={shouldShowDuration}
      account={orderPageState.account}
      orderPageState={orderPageState}
      courseSessions={courseSessions}
    />
  );

  return (
    <Box className="payment-step">
      <Grid container alignItems="flex-start" className="payment-step" mb={2}>
        <Grid item container gap={0} mt={isSmallScreen ? undefined : 2}>
          {orderPageState.returningCustomer && (
            <Grid container my={2}>
              <Typography variant="h5" fontWeight={800}>
                {t('order_page.welcomeBack', { name: orderPageState.personalDetails.firstname })}
              </Typography>
            </Grid>
          )}
          {paymentTimerDuration && (
            <Grid item container my={1}>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                  textAlign: 'center',
                  borderRadius: 1.25,
                  py: 0.25,
                  px: 2,
                  flex: 1,
                  bgcolor: Color(theme.customTheme.palette.text.primary).alpha(0.2).string(),
                }}>
                <Typography variant="body1">
                  {t('paymentTimer.prefix')}{' '}
                  <strong>
                    {t('paymentTimer.timeBold', {
                      timeMinutes: `${paymentTimerDuration.minutes().toString().padStart(2, '0')}:${paymentTimerDuration
                        .seconds()
                        .toString()
                        .padStart(2, '0')}`,
                    })}{' '}
                    {t('duration.min')}{' '}
                  </strong>{' '}
                  {t('paymentTimer.suffix')}
                </Typography>
              </Box>
            </Grid>
          )}
          {isBookingFeeActive ? (
            <Box display="flex" flexDirection="column" gap={2} width="100%">
              {orderDetailsTable}
              <Divider style={{ backgroundColor: theme.customTheme.palette.border }} />
              {renderPriceBreakdown()}
            </Box>
          ) : (
            orderDetailsTable
          )}
        </Grid>
      </Grid>
      <Divider style={{ backgroundColor: theme.customTheme.palette.border }} />
      {!isBookingFeeActive && (
        <>
          <Grid item container my={2} gap={2}>
            <OrderPageTypography bold variant="h3">
              {/* With a real deposit line right below, the price keeps its name — one deposit row is enough. */}
              {basePriceLabel(t('order_page.price'))}
            </OrderPageTypography>
            <OrderPageTypography variant="h3">
              {customerFacingPriceFormatter(orderPageState?.details.price, orderPageState.account, true)}
            </OrderPageTypography>
          </Grid>
          {hasDepositRow && (
            <Grid item container my={2} gap={2}>
              <OrderPageTypography bold variant="h3">
                {t('order_page.deposit')}
              </OrderPageTypography>
              <OrderPageTypography variant="h3">
                {priceFormatterFromCurrency(depositInfo.amount, depositInfo.currency)}
              </OrderPageTypography>
            </Grid>
          )}
          {voucherAmountUsed !== undefined && (
            <>
              <Grid item container my={2} gap={2}>
                <OrderPageTypography bold variant="h3">
                  {t('order_page.paidWithGiftCard')}
                </OrderPageTypography>
                <OrderPageTypography variant="h3">
                  {customerFacingPriceFormatter(voucherAmountUsed, orderPageState.account, true)}
                </OrderPageTypography>
              </Grid>
              <Grid item container my={2} gap={2}>
                <OrderPageTypography bold variant="h3">
                  {t('order_page.giftCardBalanceLeft')}
                </OrderPageTypography>
                <OrderPageTypography variant="h3">
                  {customerFacingPriceFormatter(voucherBalanceLeft ?? 0, orderPageState.account, true)}
                </OrderPageTypography>
              </Grid>
            </>
          )}
          {discountedPrice !== orderPageState?.details.price && (
            <Grid item container my={2} gap={2}>
              <OrderPageTypography variant="h3" color={theme.customTheme.palette.primary} bold>
                {t('order_page.priceAfterDiscount')}
              </OrderPageTypography>
              <OrderPageTypography variant="h3" color={theme.customTheme.palette.primary} bold>
                {customerFacingPriceFormatter(discountedPrice, orderPageState.account, true)}
              </OrderPageTypography>
            </Grid>
          )}
        </>
      )}
      {isTipEnabled && (
        <Grid container flexDirection="column" mb={2}>
          {!isBookingFeeActive && orderPageState?.tip?.value && orderPageState?.tip?.value > 0 && (
            <Grid container item gap={2} mb={2}>
              <OrderPageTypography bold>{t('order_page.gratuity')}:</OrderPageTypography>
              <OrderPageTypography>
                {customerFacingPriceFormatter(
                  calculateTip(orderPageState?.tip, orderPageState?.details.price),
                  account,
                )}
              </OrderPageTypography>
            </Grid>
          )}
          <TipComponent tipInitial={orderPageState.tip?.value} setTip={setTip} />
        </Grid>
      )}
      {(!isBookingFeeActive || isTipEnabled) && (
        <Divider style={{ backgroundColor: theme.customTheme.palette.border }} />
      )}
      {includes(
        [
          OrderPageIntent.BUY_NOW,
          OrderPageIntent.GIFT_CARD,
          OrderPageIntent.MONETARY_GIFT_CARD,
          OrderPageIntent.BUY_PASS,
        ],
        orderPageIntent,
      ) &&
        !isRecurringPassCheckout && (
          <Grid container flexDirection="column" my={2}>
            <Grid item mb={2}>
              <OrderPageTypography variant="h3">{t('paymentwithvoucher')}</OrderPageTypography>
            </Grid>
            <Grid item container gap={4} alignItems="center">
              <Grid item xs={isSmallScreen ? 6 : 8}>
                <OrderPageTextField
                  size="small"
                  fullWidth
                  placeholder={t('vouchercode')}
                  onChange={handleVoucherCodeChange}
                  value={voucherOrDiscountCode}
                  error={!!voucherError}
                  helperText={voucherError}
                />
              </Grid>
              <OrderPageButton
                loadingButton
                variant="outlined"
                loading={isLoadingApplyCode}
                onClick={() => submitCode()}>
                <OrderPageTypography>{t('apply')}</OrderPageTypography>
              </OrderPageButton>
              <Grid item hidden={!shouldShowVoucherSuccess}>
                <CheckCircleOutline style={{ color: '#4bbd00' }} />
              </Grid>
            </Grid>
          </Grid>
        )}

      <Grid container flexDirection="column">
        <Grid mb={2}>
          <OrderPageTypography variant="h3">{t('paymentwithcard')}</OrderPageTypography>
        </Grid>
        {shouldShowSandbox && <OrderPageTypography color="error">SANDBOX</OrderPageTypography>}
        {paymentLinkFailed && !loadingPaymentLink ? (
          <Grid container my={3} direction="column" alignItems="center" gap={2}>
            <OrderPageTypography color="error">{t('payment_form_error')}</OrderPageTypography>
            <OrderPageButton
              onClick={() => {
                void getPaymentForm();
              }}
              sx={{
                height: '50px',
                width: '200px',
                cursor: 'pointer',
                color: theme.customTheme.palette.primary,
              }}
              variant="outlined">
              <OrderPageTypography variant={'h3'} bold>
                {t('payment_form_error_retry')}
              </OrderPageTypography>
            </OrderPageButton>
          </Grid>
        ) : loadingPaymentLink || isAutoApplyingVoucher || !paymentUrl ? (
          <Grid container height="500px">
            <RotatingLoader />
          </Grid>
        ) : shouldShowPaymentsSelector && !isSquareForm ? (
          <Grid container my={3} direction="column" alignItems="center" spacing={1}>
            <Grid container my={3} alignItems="center" justifyContent={'center'} spacing={1}>
              <OrderPageTypography variant="h6">{t('order_page.installments')}</OrderPageTypography>
              <Grid item>
                <OrderPageSelect
                  value={selectedMaxPayments}
                  onChange={(event) => {
                    setSelectedMaxPayments(Number(event.target.value));
                  }}
                  style={{
                    padding: '2px 4px',
                    borderRadius: '4px',
                    border: `1px solid ${theme.customTheme.palette.border}`,
                    width: '100px',
                    color: theme.customTheme.palette.text.secondary,
                    backgroundColor: 'transparent',
                  }}>
                  {Array.from({ length: account.payment_integration_settings?.max_payments || 1 }, (_, i) => i + 1).map(
                    (num) => (
                      <FormStyledMenuItem
                        key={`MenuItem-${num}`}
                        value={num}
                        textColor={theme.customTheme.palette.text.primary}
                        backgroundColor={theme.customTheme.palette.background}>
                        <OrderPageTypography>{num}</OrderPageTypography>
                      </FormStyledMenuItem>
                    ),
                  )}
                </OrderPageSelect>
              </Grid>
            </Grid>
            <Grid item>
              <OrderPageButton
                onClick={async () => {
                  // Keep the selector up on failure -- hiding it would strand the customer on a
                  // step with no payment form and no way back to it.
                  if (await getPaymentForm()) {
                    setShouldShowPaymentsSelector(false);
                  }
                }}
                rel="noopener noreferrer"
                sx={{
                  height: '50px',
                  width: '200px',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  color: theme.customTheme.palette.primary,
                }}
                variant="outlined">
                <OrderPageTypography variant={'h3'} bold>
                  {t('open_payment_in_new_tab')}
                </OrderPageTypography>
              </OrderPageButton>
            </Grid>
          </Grid>
        ) : shouldShowButtonForPayment ? (
          <Grid item>
            <OrderPageButton
              onClick={() => {
                window.location.href = paymentUrl;
              }}
              rel="noopener noreferrer"
              sx={{
                height: '50px',
                width: '200px',
                textDecoration: 'underline',
                cursor: 'pointer',
                color: theme.customTheme.palette.primary,
              }}>
              <OrderPageTypography variant={'h3'} bold>
                {t('open_payment_in_new_tab')}
              </OrderPageTypography>
            </OrderPageButton>
          </Grid>
        ) : (
          <PaymentIframe
            paymentUrl={paymentUrl}
            stripe_integration_publishable_key={stripe_integration_publishable_key}
            stripeAccountId={stripeAccountId}
            paymentIframeSettings={orderPageState.account?.payment_integration_settings?.payment_iframe_settings}
            businessName={account.label?.[language] || account.name}
            onFormExpired={() => {
              void getPaymentForm();
            }}
          />
        )}
      </Grid>
    </Box>
  );
};

export default PaymentStep;
